/**
 * #######################################################@
 *
 * Compact filters panel
 *
 * Displays a single "Filters" button (with active filters
 * counter) in the navbar. All the filters components are
 * stacked vertically inside a popover panel, so the navbar
 * never overflows when a page defines many filters.
 *
 * Active filters are summarized as removable chips, to be
 * displayed below the navbar with <ActiveFiltersChips />.
 *
 * #######################################################@
 */
import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import lod_ from "lodash";
import { Chip, Divider, Icon, IconButton, InputAdornment, Popover, Tooltip } from "@mui/material";
import ResourceActions from "redux-react/actions/resourceActions";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import ButtonMenu from "components/Custom/ButtonMenu";
import CompactDateFilter from "components/Custom/Filters/CompactDateFilter";
import CompactSelectFilter from "components/Custom/Filters/CompactSelectFilter";
import { getLocalStorageFrontValues } from "components/Custom/Filters/filters";
import { useMaterialUIController } from "context";
import { getLabel, queryDictionary } from "helpers/dictionary";
import InputString from "hooks/filters/InputString";
import { useDispatch, useSelector } from "react-redux";
import { deleteFilter, setFilter, clearPageFilters } from "redux-react/reducers/filtersReducers";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import FiltersDialog from "pages/forms/components/FiltersDialog";
import { display } from "redux-react/reducers/snackBarReducer";

const iconsStyle = ({ palette: { dark } }) => ({
	color: () => dark.main
});

/**
 * Check if a front value must be considered as an active filter
 */
function isActiveValue(value) {
	if (lod_.isNil(value)) {
		return false;
	}
	if (lod_.isNumber(value) || lod_.isBoolean(value)) {
		return true;
	}
	return !lod_.isEmpty(value);
}

/**
 * Build a short human readable summary of a filter's value,
 * displayed in the active filters chips (values only)
 */
function summarizeValue(filter, value, language) {
	switch (filter.type) {
		case "date":
		case "datetime": {
			const format = date => {
				try {
					return new Date(date).toLocaleDateString(language);
				} catch (e) {
					return "";
				}
			};
			const start = value?.$gte ? format(value.$gte) : null;
			const end = value?.$lte ? format(value.$lte) : null;
			if (start && end) return t("FILTERS.panel.fromTo", { start, end });
			if (start) return t("FILTERS.panel.from", { start });
			if (end) return t("FILTERS.panel.to", { end });
			return "";
		}
		default: {
			if (lod_.isArray(value)) {
				const labels = value.map(item => item?.label ?? item);
				if (labels.length <= 2) {
					return labels.join(", ");
				}
				return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
			}
			if (lod_.isObject(value)) {
				// Boolean filters store the selected item object
				return `${getLabel(filter.label, language)} : ${value.label ?? ""}`;
			}
			return String(value);
		}
	}
}

/**
 * Shared hook: current page filters state + removal helpers
 */
function usePageFilters(page, filters) {
	const dispatch = useDispatch();
	const storeFilters = useSelector(state => state.filters);
	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";
	const assistantID = profile?.assistantID;

	const getFront = filter =>
		getLocalStorageFrontValues(assistantID, page, filter.attribute, storeFilters);

	const activeFilters = (filters ?? []).filter(filter => isActiveValue(getFront(filter)));

	const removeFilterFromStore = filter => {
		dispatch(
			deleteFilter({
				assistantID,
				page,
				attribute: filter.attribute
			})
		);
	};

	return { language, assistantID, getFront, activeFilters, removeFilterFromStore };
}

/**
 * Removable chips of the active filters values,
 * to display below the navbar
 */
export function ActiveFiltersChips({ page, filters }) {
	const { language, getFront, activeFilters, removeFilterFromStore } = usePageFilters(
		page,
		filters
	);

	if (!activeFilters.length) {
		return null;
	}

	return (
		<MDBox display="flex" alignItems="center" flexWrap="wrap" gap={0.75} mt={1}>
			{activeFilters.map(filter => (
				<Tooltip
					key={filter.attribute}
					placement="top"
					title={getLabel(filter.label, language) || filter.attribute}
				>
					<Chip
						size="small"
						color="info"
						variant="outlined"
						sx={{ maxWidth: 300, backgroundColor: "transparent" }}
						label={summarizeValue(filter, getFront(filter), language)}
						onClick={() => removeFilterFromStore(filter)}
						onDelete={() => removeFilterFromStore(filter)}
					/>
				</Tooltip>
			))}
		</MDBox>
	);
}

/**
 * Filters button + popover panel, to display in the navbar
 */
function FiltersPanel({ page, filters, resource = {}, loadCharts = () => {} }) {
	const dispatch = useDispatch();
	const profile = useSelector(selectCurrentProfile);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	const { language, assistantID, getFront, activeFilters, removeFilterFromStore } = usePageFilters(
		page,
		filters
	);

	const [filtersDialog, setFiltersDialog] = useState({ open: false });
	const [dictionary, setDictionary] = useState({});

	const [anchorEl, setAnchorEl] = useState(null);
	// Incremented on "clear all" to remount the filters components
	// (they hold their value in an internal state initialized from the store)
	const [resetKey, setResetKey] = useState(0);

	function setFilterToStore(filter, front, back) {
		dispatch(
			setFilter({
				assistantID,
				page,
				attribute: filter.attribute,
				front,
				back: {
					name: filter.attribute,
					value: back,
					type: filter.type,
					dictionaryPath: filter.dictionaryPath,
					links: filter.links
				}
			})
		);
	}

	const updateStoreFilter = (value, filter) => {
		if (lod_.isNil(value) || lod_.isEmpty(value)) {
			removeFilterFromStore(filter);
		} else {
			setFilterToStore(filter, value, { $in: value });
		}
	};

	const updateStoreFilterDebounce = useMemo(
		() =>
			lod_.debounce((value, filter) => {
				if (lod_.isNil(value) || lod_.isEmpty(value)) {
					dispatch(
						deleteFilter({
							assistantID,
							page,
							attribute: filter.attribute
						})
					);
				} else {
					setFilterToStore(filter, value, { $regex: value, $options: "i" });
				}
			}, 300),
		[assistantID, page]
	);

	/**
	 * Remove all filters
	 */
	const clearAllFilters = () => {
		// for (let filter of filters) {
		// 	removeFilterFromStore(filter);
		// }
		if (page && profile?.assistantID) {
			dispatch(clearPageFilters({ assistantID: profile.assistantID, page }));
		}
		setResetKey(key => key + 1);
	};

	/**
	 * Build the component for one filter, full width and
	 * compact, to be stacked in the panel
	 */
	const renderFilterControl = filter => {
		const storage = getFront(filter);

		switch (filter.type) {
			case "boolean":
				return (
					<ButtonMenu
						fullWidth
						size="small"
						title={getLabel(filter.label, language)}
						type="boolean"
						inverted={filter.inverted}
						optionnal={filter.optionnal}
						storage={storage}
						handleUpdate={items => {
							if (lod_.isEmpty(items)) {
								removeFilterFromStore(filter);
							} else {
								let value = lod_.isObject(items.value) ? items.value : { $in: [items.value] };
								setFilterToStore(filter, items, value);
							}
						}}
					/>
				);
			case "datetime":
			case "date":
				return (
					<CompactDateFilter
						assistantID={assistantID}
						page={page}
						storage={storage}
						attribute={filter.attribute}
					/>
				);
			case "code":
				return (
					<CompactSelectFilter
						items={filter.values}
						storage={storage}
						handleUpdate={items => {
							if (!items.length) {
								removeFilterFromStore(filter);
							} else {
								let values = items.map(i => i.value);
								setFilterToStore(filter, items, { $in: values });
							}
						}}
					/>
				);
			case "string":
				return (
					<InputString
						filter={filter}
						page={page}
						language={language}
						storage={storage}
						updateStoreFilter={(value, f) => updateStoreFilter(value, f)}
						updateStoreFilterDebounce={(value, f) => updateStoreFilterDebounce(value, f)}
					/>
				);
			default:
				return (
					<MDInput
						fullWidth
						size="small"
						label={getLabel(filter.label, language)}
						type={filter.type}
						value={storage ?? ""}
						onChange={e => {
							if (lod_.isNil(e.target.value) || lod_.isEmpty(e.target.value)) {
								removeFilterFromStore(filter);
							} else {
								setFilterToStore(filter, e.target.value, e.target.value);
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
									<Icon size="small">filter_alt</Icon>
								</InputAdornment>
							)
						}}
						placeholder={`Filtrer ${getLabel(filter.label, language)} ...`}
					/>
				);
		}
	};

	const getDictionary = async code => {
		if (!lod_.isNil(code)) {
			const result = await queryDictionary(dispatch, code);
			setDictionary(result);
		}
	};

	const saveFilters = filters => {
		dispatch(
			ResourceActions.updateResourceConfiguration(resource.code, { filters }, () => {
				dispatch(
					display({
						type: "success",
						message: t("SETTINGS.FILTERS.filtersSaved")
					})
				);
				setFiltersDialog({ open: false });

				if (page && profile?.assistantID) {
					dispatch(clearPageFilters({ assistantID: profile.assistantID, page }));
				}

				loadCharts();
			})
		);
	};

	useEffect(() => {
		if (!lod_.isEmpty(resource?.format?.dictionary) && !lod_.isNil(resource?.format?.dictionary)) {
			getDictionary(resource?.format?.dictionary);
		}
	}, [resource]);

	return (
		<MDBox display="flex" alignItems="center">
			{/* Trigger button */}
			<MDButton
				variant="outlined"
				color="dark"
				onClick={e => setAnchorEl(e.currentTarget)}
				aria-haspopup="true"
			>
				<Icon>filter_list</Icon>&nbsp;{t("FILTERS.panel.title")}&nbsp;({activeFilters.length})
			</MDButton>

			{/* Filters panel */}
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: {
						sx: ({ palette, borders, boxShadows }) => ({
							width: 400,
							maxWidth: "90vw",
							maxHeight: "75vh",
							overflowY: "auto",
							p: 1.5,
							backgroundColor: darkMode ? palette.background.card : palette.white.main,
							border: `${borders.borderWidth[1]} solid ${
								darkMode ? palette.grey[700] : palette.grey[300]
							}`,
							boxShadow: boxShadows.lg
						})
					}
				}}
			>
				{/* Panel header */}
				<MDBox display="flex" justifyContent="space-between" alignItems="center">
					<MDTypography variant="h6">{t("FILTERS.panel.title")}</MDTypography>
					<MDBox>
						<MDButton variant="outlined" color="error" size="small" onClick={clearAllFilters}>
							Déselectionner
						</MDButton>
						{/* Filters */}

						<MDButton
							sx={{ ml: 1 }}
							variant="outlined"
							color="info"
							size="small"
							onClick={() => setFiltersDialog({ open: true })}
						>
							Modifier
						</MDButton>
					</MDBox>
					<FiltersDialog
						filtersDialog={filtersDialog}
						setFiltersDialog={setFiltersDialog}
						dictionary={{ [resource.format?.dictionary]: dictionary }}
						filters={resource.filters ?? []}
						onSave={saveFilters}
						page={page}
					/>
				</MDBox>
				<Divider sx={{ mt: 0.5, mb: 1 }} />

				{/* Filters list */}
				{filters.map(filter => (
					<MDBox key={`${filter.attribute}-${resetKey}`} mb={1}>
						<MDTypography
							variant="caption"
							fontWeight="bold"
							color="text"
							textTransform="capitalize"
						>
							{getLabel(filter.label, language) || filter.attribute}
						</MDTypography>
						<MDBox mt={0.25}>{renderFilterControl(filter)}</MDBox>
					</MDBox>
				))}
			</Popover>
		</MDBox>
	);
}

export default FiltersPanel;
